import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { validateCommonCode } from './helper';
import TenantContext from '../../lib/context/TenantContext';
import { addEntity, getByPartitionKey } from '../../lib/services/api-service';
import { TableName } from '../../constants/table';
import { FormInputText } from '../formComponent/FormInputText';
import { FormInputNumber } from '../formComponent/FormInputNumber';
import { useGetAllCommonCode } from '../../lib/query/commonCode';

interface IFormInput {
  commonCode: string;
  commonCodeOCR: string;
  vendorGST: string;
  chartOfAccount: string;
  voucherName: string;
  tdsThreshold: number;
  companyId: string;
  tdsResidualAmount: number;
  tdsRate: number;
}

interface ICompany {
  id: string;
  name: string;
  status: string;
}

const AddCommonCode = (props: {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { tenant } = React.useContext(TenantContext);
  const { refetch } = useGetAllCommonCode(tenant.id);
  const [companies, setCompanies] = React.useState<ICompany[]>([]);
  const [isCustom, setIsCustom] = React.useState(false); // New state to track Custom selection
  const [isCustomVoucherName, setIsCustomVoucherName] = React.useState(false);

  React.useEffect(() => {
    const fetchCompanies = async () => {
      const companyMasters = await getByPartitionKey(
        TableName.CompanyMaster,
        tenant.id,
      );

      const uniqueCompanies = new Map<string, ICompany>();
      companyMasters.forEach((company: any) => {
        if (
          company.Active === true &&
          !uniqueCompanies.has(company.CompanyName)
        ) {
          uniqueCompanies.set(company.CompanyName, {
            id: company.RowKey,
            name: company.CompanyName,
            status: company.Active,
          });
        }
      });
      const sortedCompanies = Array.from(uniqueCompanies.values()).sort(
        (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      );
      setCompanies(sortedCompanies);
    };
    fetchCompanies();
  }, [tenant.id]);

  const schema = yup.object().shape({
    commonCode: yup
      .string()
      .required('Vendor Name is required')
      .test(
        'cc',
        'Vendor already exists use edit functionality instead',
        async (value, ctx) => {
          const response = await validateCommonCode(tenant.id, value);
          if (!response) {
            ctx.createError({
              message: 'Vendor already exists use edit functionality instead',
              type: 'required',
            });
          }
          return response;
        },
      ),
    commonCodeOCR: yup.string().required('Vendor OCR Name is required'),
    chartOfAccount: yup.string().required('Chart of Account is required'),
    voucherName: yup.string().required('Voucher Name is required'),
    vendorGST: yup.string().required('Vendor GST is required'),
    tdsThreshold: yup.number().required('TDS Threshold is required'),
    tdsResidualAmount: yup.number().required('TDS Residual Amount is required'),
    tdsRate: yup.number().required('TDS Rate is required'),
    companyId: yup.string().required('Company is required'),
  });

  const { handleSubmit, control, reset } = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: IFormInput) => {
    try {
      const commonCodeMappings = await getByPartitionKey(
        TableName.CommonCodeMapping,
        tenant.id.toLowerCase(),
      );
      const commonCode = await getByPartitionKey(
        TableName.CommonCode,
        tenant.id.toLowerCase(),
      );

      const existingCommonCodeMapping = commonCodeMappings.find(
        (mapping: any) => mapping.CommonCodeOCR === data.commonCode,
      );

      const existingCommonCode = commonCode.find(
        (mapping: any) => mapping.CompanyId === data.companyId,
      );

      if (existingCommonCodeMapping && existingCommonCode) {
        toast.warn('Entry already exists');
        return;
      }

      const commonCodeUUID = uuidv4();
      const commonCodeMappingUUID = uuidv4();

      const commonCodeData = {
        partitionKey: tenant.id.toLowerCase(),
        rowKey: commonCodeUUID,
        CommonCode: data.commonCode,
        CommonCodeName: data.commonCodeOCR,
        ChartOfAccount: data.chartOfAccount,
        VoucherName: data.voucherName,
        TDSThreshold: data.tdsThreshold,
        TDSResidualAmount: data.tdsResidualAmount,
        TDSRate: data.tdsRate,
        VendorGST: data.vendorGST,
        CompanyId: data.companyId,
      };

      const commonCodeMappingData = {
        partitionKey: tenant.id.toLowerCase(),
        rowKey: commonCodeMappingUUID,
        CommonCodeId: commonCodeUUID,
        CommonCodeOCR: data.commonCodeOCR,
      };

      await Promise.all([
        addEntity(TableName.CommonCode, commonCodeData),
        addEntity(TableName.CommonCodeMapping, commonCodeMappingData),
      ]);

      toast.success(
        `Successfully added new Common Code with name: ${data.commonCode}`,
      );
      refetch();
      props.setShowDialog(false);
      reset();
    } catch (err) {
      toast.error('something went wrong');
    }
  };

  const handleClose = () => {
    props.setShowDialog(false);
    reset();
  };

  return (
    <React.Fragment>
      <Dialog open={props.showDialog} onClose={handleClose}>
        <DialogTitle>
          <div className="text-2xl text-center font-bold">ADD VENDOR</div>
        </DialogTitle>

        <DialogContent sx={{ width: '400px' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-4">
              <FormControl fullWidth>
                <InputLabel id="company-select-label">Company Name</InputLabel>
                <Controller
                  name="companyId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="company-select-label"
                      label="CompanyName">
                      {companies.map(company => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </div>
            <div className="mt-4">
              <FormInputText
                name="commonCode"
                control={control}
                label="Vendor Name"
              />
            </div>
            <div className="mt-4">
              <FormInputText
                name="commonCodeOCR"
                control={control}
                label="Vendor OCR Name"
              />
            </div>
            <div className="mt-4">
              <FormControl fullWidth>
                {/* Single InputLabel for Chart of Account */}
                <InputLabel id="chart-of-account-label">
                  Chart Of Account
                </InputLabel>
                <Controller
                  name="chartOfAccount"
                  control={control}
                  render={({ field }) =>
                    isCustom ? (
                      // Show a text input field if "Custom" is selected
                      <FormInputText label={''} control={control} {...field} />
                    ) : (
                      // Otherwise show the dropdown
                      <Select
                        {...field}
                        labelId="chart-of-account-label"
                        onChange={e => {
                          field.onChange(e);
                          if (e.target.value === 'Custom') {
                            setIsCustom(true);
                          } else {
                            setIsCustom(false);
                          }
                        }}>
                        <MenuItem value="Purchase">Purchase</MenuItem>
                        <MenuItem value="Sales">Sales</MenuItem>
                        <MenuItem value="Abnormal">Abnormal</MenuItem>
                        <MenuItem value="Custom">Custom</MenuItem>
                      </Select>
                    )
                  }
                />
              </FormControl>
            </div>

            <div className="mt-4">
              <FormControl fullWidth>
                <InputLabel id="voucher-name-label">Voucher Name</InputLabel>
                <Controller
                  name="voucherName"
                  control={control}
                  render={({ field }) =>
                    isCustomVoucherName ? (
                      <FormInputText label={''} control={control} {...field} />
                    ) : (
                      <Select
                        {...field}
                        labelId="voucher-name-label"
                        onChange={e => {
                          field.onChange(e);
                          if (e.target.value === 'Custom') {
                            setIsCustomVoucherName(true);
                          } else {
                            setIsCustomVoucherName(false);
                          }
                        }}>
                        <MenuItem value="Purchase">Purchase</MenuItem>
                        <MenuItem value="Sales">Sales</MenuItem>
                        <MenuItem value="Custom">Custom</MenuItem>
                      </Select>
                    )
                  }
                />
              </FormControl>
            </div>
            <div className="mt-4">
              <FormInputText
                name="vendorGST"
                control={control}
                label="Vendor GST"
              />
            </div>
            <div className="mt-4">
              <FormInputNumber
                name="tdsThreshold"
                control={control}
                label="TDS Threshold"
              />
            </div>
            <div className="mt-4">
              <FormInputNumber
                name="tdsResidualAmount"
                control={control}
                label="TDS Residual Amount"
              />
            </div>
            <div className="mt-4">
              <FormInputNumber
                name="tdsRate"
                control={control}
                label="TDS Rate"
              />
            </div>

            <div className="mt-6 flex items-end justify-end space-x-4">
              <Button onClick={handleClose} variant="contained">
                Discard
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default AddCommonCode;
